import { z } from "zod";

import type { NotNeededOutput, StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import { StepResultType, type StepResult } from "@/src/lib/workflows/steps/types";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";

const notNeededOutputSchema = z.object({ notNeeded: z.literal(true) });

/**
 * The result of a step that had nothing to do and whose result no later step reads: a wrapper skipped
 * it, or it ran and found no work. Log why under the task and step; the output records only that it
 * wasn't needed. A no-work result that later steps branch on belongs in the step's own output type.
 */
export function notNeededResult(): StepResult {
  const output: NotNeededOutput = { notNeeded: true };

  return { type: StepResultType.COMPLETED, output };
}

/** Whether a step's output (in a run, or saved on its row) says the step had nothing to do. */
export function isNotNeededOutput(output: unknown): output is NotNeededOutput {
  return notNeededOutputSchema.safeParse(output).success;
}

/**
 * The outputs saved on a task's rows, as later steps read them. A row leaves its step's output missing
 * when it has none, when it is marked not needed, or when the step failed with no retry owed: a
 * failure's output holds only its error, never a result. A failed row still owed a retry keeps the
 * prior output the retry resumes from.
 */
export function stepOutputsFromRows(
  rows: readonly Pick<TaskStep, "stepKey" | "status" | "output" | "remainingRetries">[]
): StepOutputs {
  return rows.reduce<StepOutputs>(
    (acc, row) =>
      row.output &&
      !isNotNeededOutput(row.output) &&
      !(row.status === TaskStepStatus.FAILED && row.remainingRetries === 0)
        ? { ...acc, [row.stepKey]: row.output }
        : acc,
    {}
  );
}
